/**
 * SSE client for /api/arena/debate/turn.
 *
 * Wraps fetch + ReadableStream parsing into a small async-iterator-style
 * callback API. The viewer hands us a request body + an onDelta callback;
 * we resolve when the server emits `done` (or reject on error / abort).
 */

import type { DebateTurnEvent, DebateTurnRequest } from '@arena/shared';

// Default to a same-origin path so the browser doesn't see a cross-origin
// fetch (which can trigger Safari / macOS Local Network / firewall prompts
// and surface as "Failed to fetch"). Vite proxies /api/* to the wrangler
// dev worker; in prod the API is colocated under the same hostname.
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export interface StreamTurnOptions {
  body: DebateTurnRequest;
  onDelta: (text: string) => void;
  signal?: AbortSignal;
}

export async function streamDebateTurn(opts: StreamTurnOptions): Promise<void> {
  const res = await fetch(`${API_BASE}/api/arena/debate/turn`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(opts.body),
    signal: opts.signal,
  });

  if (!res.ok || !res.body) {
    let detail = `HTTP ${res.status}`;
    try {
      const payload = (await res.json()) as { error?: string; detail?: string };
      detail = payload.detail ?? payload.error ?? detail;
    } catch {
      // not JSON — keep HTTP status
    }
    throw new Error(detail);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // Process the SSE stream chunk by chunk.
  let done = false;
  while (!done) {
    const result = await reader.read();
    done = result.done;
    if (done) return;
    buffer += decoder.decode(result.value, { stream: true });

    let blank: number;
    while ((blank = buffer.indexOf('\n\n')) !== -1) {
      const rawEvent = buffer.slice(0, blank);
      buffer = buffer.slice(blank + 2);
      const dataLine = rawEvent.split('\n').find((line) => line.startsWith('data:'));
      if (!dataLine) continue;
      const payload = dataLine.slice(5).trim();
      if (!payload) continue;
      let event: DebateTurnEvent;
      try {
        event = JSON.parse(payload) as DebateTurnEvent;
      } catch {
        continue;
      }
      if (event.type === 'delta') {
        opts.onDelta(event.text);
      } else if (event.type === 'done') {
        return;
      } else if (event.type === 'error') {
        throw new Error(event.message);
      }
    }
  }
}
